import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LogOut, Users, UserCheck, Layers, RefreshCw, Download } from "lucide-react";

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div
      className="border border-white/10 rounded-xl p-6 bg-white/[0.02]"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400">
          {label}
        </div>
        <Icon className="h-4 w-4 text-[#FFD700]" />
      </div>
      <div className="mt-3 font-display text-4xl tracking-tight">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, one_on_one: 0, group: 0, either: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, appsRes, statsRes] = await Promise.all([
        http.get("/auth/me"),
        http.get("/admin/applications"),
        http.get("/admin/stats"),
      ]);
      setUser(meRes.data);
      setApps(appsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/admin/login");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await http.post("/auth/logout");
    } catch (_) {}
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const exportCsv = () => {
    if (!apps.length) return;
    const headers = ["created_at", "name", "phone", "email", "class_level", "mentorship_type", "current_score", "goal"];
    const csv =
      headers.join(",") +
      "\n" +
      apps
        .map((r) =>
          headers
            .map((h) => {
              const v = r[h] ?? "";
              return `"${String(v).replace(/"/g, '""')}"`;
            })
            .join(",")
        )
        .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `applications-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-[#FFD700] flex items-center justify-center text-black font-display font-bold">
              S
            </div>
            <div className="leading-tight">
              <div className="font-display text-base tracking-tight">Admin Dashboard</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400">
                {user?.email || "…"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={load}
              data-testid="admin-refresh-button"
              variant="ghost"
              className="text-gray-300 hover:text-white hover:bg-white/5 h-10"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={exportCsv}
              data-testid="admin-export-button"
              className="bg-white/10 hover:bg-white/20 text-white h-10"
            >
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button
              onClick={logout}
              data-testid="admin-logout-button"
              className="bg-[#FF3B30] hover:bg-[#E6352B] text-white h-10"
            >
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <h1 className="font-display text-3xl md:text-4xl tracking-tight">Mentorship Applications</h1>
        <p className="mt-2 text-gray-400 text-sm">
          Leads submitted from the landing page. Follow up within 24 hours.
        </p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Users} label="Total Applications" value={stats.total} testId="stat-total" />
          <Stat icon={UserCheck} label="1:1 Interest" value={stats.one_on_one} testId="stat-1on1" />
          <Stat icon={Layers} label="Group Interest" value={stats.group} testId="stat-group" />
          <Stat icon={Users} label="Flexible / Either" value={stats.either} testId="stat-either" />
        </div>

        <div className="mt-10 border border-white/10 rounded-xl overflow-hidden bg-[#0f0f0f]">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-white/10 hover:bg-transparent">
                <TableHead className="text-gray-400 uppercase text-[10px] tracking-[0.2em]">Date</TableHead>
                <TableHead className="text-gray-400 uppercase text-[10px] tracking-[0.2em]">Name</TableHead>
                <TableHead className="text-gray-400 uppercase text-[10px] tracking-[0.2em]">Phone</TableHead>
                <TableHead className="text-gray-400 uppercase text-[10px] tracking-[0.2em]">Email</TableHead>
                <TableHead className="text-gray-400 uppercase text-[10px] tracking-[0.2em]">Class</TableHead>
                <TableHead className="text-gray-400 uppercase text-[10px] tracking-[0.2em]">Track</TableHead>
                <TableHead className="text-gray-400 uppercase text-[10px] tracking-[0.2em]">Score</TableHead>
                <TableHead className="text-gray-400 uppercase text-[10px] tracking-[0.2em]">Goal</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && apps.length === 0 && (
                <TableRow data-testid="admin-empty-state">
                  <TableCell colSpan={8} className="text-center text-gray-500 py-10">
                    No applications yet.
                  </TableCell>
                </TableRow>
              )}
              {apps.map((a) => (
                <TableRow
                  key={a.id}
                  className="border-b border-white/5 hover:bg-white/[0.02]"
                  data-testid={`admin-row-${a.id}`}
                >
                  <TableCell className="text-xs text-gray-400 whitespace-nowrap">
                    {new Date(a.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell className="text-gray-300">{a.phone}</TableCell>
                  <TableCell className="text-gray-300">{a.email || "—"}</TableCell>
                  <TableCell className="text-gray-300">{a.class_level}</TableCell>
                  <TableCell>
                    <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/25">
                      {a.mentorship_type}
                    </span>
                  </TableCell>
                  <TableCell className="text-gray-300">{a.current_score || "—"}</TableCell>
                  <TableCell className="text-gray-400 max-w-[240px] truncate" title={a.goal || ""}>
                    {a.goal || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
